import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FocusEvent, useCallback, useMemo, useState } from 'react';
import { SubmitHandler, UseFormSetValue } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { NewTicketFormValuesT } from '../declarations';
import { getLineItemsApi } from 'api/repairOrder';
import { useTickets } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { ApiExistingTicketErrorI } from 'declarations/interfaces/errors';
import { LineItemI } from 'declarations/interfaces/lineItems';
import { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import { NoopT } from 'declarations/types/common';
import { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getExistingId, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchClaimStatuses } from 'hooks/api/useClaimStatuses';
import { useFetchTicketPriorities } from 'hooks/api/useTicketPriorities';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import { useFetchTicketRoExists } from 'hooks/api/useTicketRoNumber';
import { useCreateTicket } from 'hooks/api/useTickets';

type UseOpenNewTicketPopupT = {
  requestCategoriesOptions: SelectOptionT[];
  isLoadingRequestCategoriesOptions: boolean;
  claimStatusesOptions: SelectOptionT[];
  isLoadingClaimStatusesOptions: boolean;
  ticketPrioritiesOptions: SelectOptionT[];
  isLoadingTicketPrioritiesOptions: boolean;
  isLoadingRoofTopOptions: boolean;
  roofTopIdsOptions: SelectOptionT[];
  isPopupOpened: boolean;
  isTicketSuccessfullyCreated: boolean;
  handleSubmitForm: SubmitHandler<NewTicketFormValuesT>;
  handleAmountBlur: (
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => (e: FocusEvent<HTMLInputElement>) => void;
  isCreatingNewTicket: boolean;
  isRoWarningPopupOpen: boolean;
  createTicketErrorMessage: string | undefined;
  createTicketValidationErrorMessage: string | undefined;
  showOpenTicketForm: boolean;
  isTicketCreationError: boolean;
  handleCloseNewTicketPopup: NoopT;
  handleRoofTopChange: (roofTopId: string) => void;
  createTicketExistingId: string | null;
  ticketRequestCategories: TicketRequestCategoryI[] | undefined;
  handleNavigateToExistingTicketDetails: () => void;
  advisorNumber: string | undefined;
  checkIfRoExists: (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => void;
  handleLineNumberChange: (lineNumber: string) => void;
  handleROChange: (roNumber: string) => void;
  lineItemsObj: LineItemI[];
  lineNumbers: { value: string; label: string }[];
  recentRoData: { roofTopId: string | null; roNumber: string | null };
  setAdvisorNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsRoWarningPopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLineItemsObj: React.Dispatch<React.SetStateAction<LineItemI[]>>;
  setLineNumbers: React.Dispatch<React.SetStateAction<{ value: string; label: string }[]>>;
  setRecentRoData: React.Dispatch<
    React.SetStateAction<{ roofTopId: string | null; roNumber: string | null }>
  >;
  setShowLineNumberDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedLineNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSelectedRoofTopId: React.Dispatch<React.SetStateAction<string | undefined>>;
  showLineNumberDropdown: boolean;
};

const useOpenNewTicketPopup = (): UseOpenNewTicketPopupT => {
  /**
   * Hooks
   */
  const { setNewTicketPopupVisibility, isNewTicketPopupOpened } = useTickets();
  const [selectedRoofTopId, setSelectedRoofTopId] = useState<string | undefined>(undefined);
  const { userProfile, isLoadingUserProfile } = useUserProfile();
  const queryClient = useQueryClient();
  const { setTicketId } = useTickets();
  const [, setSelectedRoNumber] = useState<string | null>(null);
  const [, setSelectedLineNumber] = useState<string | undefined>(undefined);
  const [isRoWarningPopupOpen, setIsRoWarningPopupOpen] = useState(false);
  const [showLineNumberDropdown, setShowLineNumberDropdown] = useState(false);
  const [advisorNumber, setAdvisorNumber] = useState<string | undefined>(undefined);
  const [lineNumbers, setLineNumbers] = useState<{ value: string; label: string }[]>([]);
  const [lineItemsObj, setLineItemsObj] = useState<LineItemI[]>([]);
  const [recentRoData, setRecentRoData] = useState<{
    roofTopId: string | null;
    roNumber: string | null;
  }>({
    roofTopId: null,
    roNumber: null,
  });
  /**
   * Api hooks
   */
  const ticketRequestCategoriesResult = useFetchTicketRequestCategories(
    isNewTicketPopupOpened,
    selectedRoofTopId
  );
  let [searchParams] = useSearchParams();
  const roofTopId = searchParams.get(TicketsRefinementKeysE.RoofTopId) || selectedRoofTopId || '';
  const claimStatusesRequestParams = {
    isEnabled: isNewTicketPopupOpened,
    roofTopId: roofTopId,
  };

  const claimStatusesResult = useFetchClaimStatuses(claimStatusesRequestParams);
  const ticketPrioritiesResult = useFetchTicketPriorities(isNewTicketPopupOpened);
  const createTicketMutation = useCreateTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
    },
  });
  const roQuery = useFetchTicketRoExists(
    recentRoData.roofTopId as string,
    recentRoData.roNumber as string
  );

  /**
   * Variables
   */
  const requestCategoriesOptions = useMemo(
    () =>
      ticketRequestCategoriesResult.data
        ? ticketRequestCategoriesResult.data?.map(({ id, categoryName }) => ({
            label: categoryName,
            value: id,
          }))
        : [],
    [ticketRequestCategoriesResult.data]
  );
  const claimStatusesOptions = useMemo(
    () =>
      claimStatusesResult.data
        ? claimStatusesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [claimStatusesResult.data]
  );
  const ticketPrioritiesOptions = useMemo(
    () =>
      ticketPrioritiesResult.data
        ? ticketPrioritiesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketPrioritiesResult.data]
  );
  const roofTopIdsOptions = useMemo(
    () =>
      userProfile
        ? userProfile?.mappedRoofTops
            .map(({ id, name }) => ({ label: name, value: id }))
            .sort((a, b) => {
              if (a.label > b.label) {
                return 1;
              }

              if (a.label < b.label) {
                return -1;
              }

              return 0;
            })
        : [],
    [userProfile]
  );
  const createTicketErrorMessage = useMemo(
    () => getErrorMessage(createTicketMutation.error),
    [createTicketMutation.error]
  );
  const createTicketValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(createTicketMutation.error),
    [createTicketMutation.error]
  );
  const createTicketExistingId = useMemo(
    () => getExistingId(createTicketMutation.error as ApiExistingTicketErrorI),
    [createTicketMutation.error]
  );
  /**
   * Handlers
   */
  const resetTicketMutationState = useCallback(() => {
    if (createTicketMutation.isSuccess || createTicketMutation.isError) {
      createTicketMutation.reset();
    }
  }, [createTicketMutation]);
  const handleCloseNewTicketPopup = useCallback(() => {
    setNewTicketPopupVisibility(false);
    resetTicketMutationState();
  }, [resetTicketMutationState, setNewTicketPopupVisibility]);
  const setAmountValue = (value: string, setValue: UseFormSetValue<NewTicketFormValuesT>) => {
    setValue('amount', value, { shouldValidate: true });
  };
  const handleAmountBlur = useCallback(
    (setValue: UseFormSetValue<NewTicketFormValuesT>) => (e: FocusEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const [numbersBeforeDot, numbersAfterDot] = value.split('.');
      const hasOneDecimal = !!numbersAfterDot && numbersAfterDot.length === 1;
      const hasTwoDecimal = !!numbersAfterDot && numbersAfterDot.length >= 2;

      if (!value) {
        setAmountValue('0.00', setValue);
      } else if (value && hasOneDecimal) {
        setAmountValue(`${numbersBeforeDot}.${numbersAfterDot}0`, setValue);
      } else if (value && !hasTwoDecimal) {
        setAmountValue(`${numbersBeforeDot}.00`, setValue);
      }
    },
    []
  );
  const handleSubmitForm = useCallback(
    (data: NewTicketFormValuesT) => {
      createTicketMutation.mutate({
        ...data,
        roDate: format(data.roDate, 'yyyy-MM-dd'),
        amount: Number(data.amount),
        claimSubmittedDate: data.claimSubmittedDate
          ? format(data.claimSubmittedDate, 'yyyy-MM-dd')
          : null,
        subCategoryId: data.subCategoryId,
      });
    },
    [createTicketMutation]
  );
  const handleRoofTopChange = (roofTopId: string) => {
    setSelectedRoofTopId(roofTopId);
    setRecentRoData(prevRoData => ({
      ...prevRoData,
      roofTopId: roofTopId,
    }));
  };
  const handleROChange = (roNumber: string | null) => {
    setSelectedRoNumber(roNumber);
    setRecentRoData(prevRoData => ({
      ...prevRoData,
      roNumber: roNumber,
    }));
  };

  const handleLineNumberChange = (lineNumber: string) => {
    setSelectedLineNumber(recentRoData.roNumber ? lineNumber : '');
  };

  const handleNavigateToExistingTicketDetails = () => {
    setTicketId(createTicketExistingId);
  };
  const setAdvisorLineNumber = async (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => {
    setRecentRoData({ roofTopId, roNumber });
    const roResult = roQuery.data;
    setValue('advisorNumber', roResult?.serviceAdvisorNumber);
    setAdvisorNumber(roResult?.serviceAdvisorNumber);
    if (roQuery.isError || (roNumber && roQuery.data === undefined)) {
      setIsRoWarningPopupOpen(true);
      setShowLineNumberDropdown(false);
      setSelectedLineNumber(undefined);
      setValue('lineNumber', '');
      setLineItemsObj([]);
    } else {
      const lineItems = getLineItemsApi(roofTopId, roNumber);
      setLineItemsObj(await lineItems);

      const mappedLineItems = (await lineItems).map(item => {
        return {
          value: item.lineNumber,
          label: item.lineNumber,
        };
      });
      if (mappedLineItems.length > 0) {
        setLineNumbers(mappedLineItems);
        setShowLineNumberDropdown(true);
      } else {
        setShowLineNumberDropdown(false);
      }
    }
  };

  const checkIfRoExists = async (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => {
    if (!roofTopId && !roNumber) {
      return;
    }
    if (roofTopId && roNumber && roNumber !== null) {
      setAdvisorLineNumber(roofTopId, roNumber, setValue);
    }
  };

  return {
    requestCategoriesOptions,
    isLoadingRequestCategoriesOptions:
      ticketRequestCategoriesResult.isLoading && !!selectedRoofTopId,
    claimStatusesOptions,
    isLoadingClaimStatusesOptions: claimStatusesResult.isLoading,
    ticketPrioritiesOptions,
    isLoadingTicketPrioritiesOptions: ticketPrioritiesResult.isLoading,
    isLoadingRoofTopOptions: isLoadingUserProfile,
    roofTopIdsOptions,
    handleCloseNewTicketPopup,
    isPopupOpened: isNewTicketPopupOpened,
    handleSubmitForm,
    handleAmountBlur,
    isTicketSuccessfullyCreated: createTicketMutation.isSuccess,
    showOpenTicketForm: createTicketMutation.isIdle || createTicketMutation.isLoading,
    isTicketCreationError: createTicketMutation.isError,
    isCreatingNewTicket: createTicketMutation.isLoading,
    createTicketErrorMessage,
    createTicketValidationErrorMessage,
    handleRoofTopChange,
    createTicketExistingId,
    ticketRequestCategories: ticketRequestCategoriesResult.data,
    handleNavigateToExistingTicketDetails,
    advisorNumber,
    checkIfRoExists,
    handleLineNumberChange,
    handleROChange,
    lineItemsObj,
    lineNumbers,
    recentRoData,
    setAdvisorNumber,
    setIsRoWarningPopupOpen,
    setLineItemsObj,
    setLineNumbers,
    setRecentRoData,
    setSelectedLineNumber,
    setShowLineNumberDropdown,
    showLineNumberDropdown,
    isRoWarningPopupOpen,
    setSelectedRoofTopId,
  };
};

export default useOpenNewTicketPopup;
